import type { SupabaseClient, User } from '@supabase/supabase-js'

import { supabaseProjectRefFromUrl } from './supabaseProject'
import {
  beginVisibilityWakeRecovery,
  captureAuthRefreshTokenFailure,
  captureAuthUnauthorizedFailure,
} from './authSentryReporting'

const AUTH_TOKEN_SUFFIX = '-auth-token'
export const BILLING_EXTERNAL_NAV_KEY = 'applicator:billing-external-nav'
export const AUTH_SESSION_BACKUP_KEY = 'applicator:auth-session-backup'

type PersistedAuthPayload = {
  access_token?: string
  refresh_token?: string
  expires_at?: number
  user?: User | null
}

export function supabaseAuthStorageKey(projectRef: string): string {
  return `sb-${projectRef}${AUTH_TOKEN_SUFFIX}`
}

export function readPersistedAuthSessionRaw(projectRef: string): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(supabaseAuthStorageKey(projectRef))
}

export function parsePersistedAuthPayload(projectRef: string): PersistedAuthPayload | null {
  const raw = readPersistedAuthSessionRaw(projectRef)
  if (!raw) return null
  return parseAuthPayloadRaw(raw)
}

function parseAuthPayloadRaw(raw: string): PersistedAuthPayload | null {
  try {
    return JSON.parse(raw) as PersistedAuthPayload
  } catch {
    return null
  }
}

export function readAuthSessionBackupRaw(): string | null {
  if (typeof window === 'undefined') return null
  return sessionStorage.getItem(AUTH_SESSION_BACKUP_KEY)
}

export function readRecoveryAuthPayload(projectRef: string): PersistedAuthPayload | null {
  return parsePersistedAuthPayload(projectRef) ?? (
    readAuthSessionBackupRaw() ? parseAuthPayloadRaw(readAuthSessionBackupRaw()!) : null
  )
}

export function readPersistedAuthUser(projectRef: string): User | null {
  return readRecoveryAuthPayload(projectRef)?.user ?? null
}

export function projectRefFromEnv(): string {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
  return url ? supabaseProjectRefFromUrl(url) : ''
}

export function backupAuthSessionBeforeExternalRedirect(
  projectRef: string = projectRefFromEnv(),
): void {
  if (typeof window === 'undefined') return
  const raw = readPersistedAuthSessionRaw(projectRef)
  if (raw) {
    sessionStorage.setItem(AUTH_SESSION_BACKUP_KEY, raw)
  }
}

export function clearAuthSessionBackup(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(AUTH_SESSION_BACKUP_KEY)
}

export function markBillingExternalNavigation(): void {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(BILLING_EXTERNAL_NAV_KEY, String(Date.now()))
}

export function clearBillingExternalNavigation(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(BILLING_EXTERNAL_NAV_KEY)
}

export function hasRecentBillingExternalNavigation(maxAgeMs = 30 * 60 * 1000): boolean {
  if (typeof window === 'undefined') return false
  const raw = sessionStorage.getItem(BILLING_EXTERNAL_NAV_KEY)
  if (!raw) return false
  const ts = Number(raw)
  if (!Number.isFinite(ts)) return false
  return Date.now() - ts <= maxAgeMs
}

async function restoreSessionFromPersistedPayload(
  client: SupabaseClient,
  payload: PersistedAuthPayload,
): Promise<User | null> {
  if (payload.access_token && payload.refresh_token) {
    const { data, error } = await client.auth.setSession({
      access_token: payload.access_token,
      refresh_token: payload.refresh_token,
    })
    if (!error && data.session?.user) {
      return data.session.user
    }
  }

  return payload.user ?? null
}

/** Validate session before leaving the app tab (Stripe, OAuth hops). Never refresh here — it can wipe storage. */
export async function ensureSessionPersistedBeforeExternalRedirect(
  client: SupabaseClient,
  projectRef: string = projectRefFromEnv(),
): Promise<void> {
  const { data: { session } } = await client.auth.getSession()
  if (session?.access_token) {
    backupAuthSessionBeforeExternalRedirect(projectRef)
    return
  }

  if (readPersistedAuthSessionRaw(projectRef)) {
    backupAuthSessionBeforeExternalRedirect(projectRef)
  }
}

/**
 * After browser Back from a third-party checkout, restore via setSession first.
 * refreshSession runs only as a last resort — a failed refresh can clear localStorage.
 */
export async function recoverSessionFromStorage(
  client: SupabaseClient,
  projectRef: string = projectRefFromEnv(),
): Promise<User | null> {
  const payload = readRecoveryAuthPayload(projectRef)
  if (!payload) {
    return null
  }

  const { data: { session: existing } } = await client.auth.getSession()
  if (existing?.user) {
    clearBillingExternalNavigation()
    clearAuthSessionBackup()
    return existing.user
  }

  const restored = await restoreSessionFromPersistedPayload(client, payload)
  if (restored) {
    clearBillingExternalNavigation()
    clearAuthSessionBackup()
    return restored
  }

  const { data: { session }, error: sessionError } = await client.auth.getSession()
  if (!sessionError && session?.user) {
    clearBillingExternalNavigation()
    clearAuthSessionBackup()
    return session.user
  }

  if (payload.refresh_token) {
    const { data, error } = await client.auth.refreshSession()
    if (!error && data.session?.user) {
      clearBillingExternalNavigation()
      clearAuthSessionBackup()
      return data.session.user
    }
    if (error) {
      const status = typeof error.status === 'number' ? error.status : 400
      captureAuthRefreshTokenFailure(status, error.message)
      if (status === 400) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(supabaseAuthStorageKey(projectRef))
        }
        clearAuthSessionBackup()
      }
    }
  }

  return payload.user ?? null
}

/** Run once at app bootstrap so RequireAuth does not redirect before recovery finishes. */
export function bootstrapAuthSessionRecovery(
  client: SupabaseClient,
  projectRef: string = projectRefFromEnv(),
): Promise<void> {
  if (typeof window === 'undefined' || !projectRef) {
    return Promise.resolve()
  }

  const shouldRecover =
    Boolean(readPersistedAuthSessionRaw(projectRef))
    || Boolean(readAuthSessionBackupRaw())
    || hasRecentBillingExternalNavigation()

  if (!shouldRecover) {
    return Promise.resolve()
  }

  return recoverSessionFromStorage(client, projectRef).then(() => undefined)
}

function shouldAttemptExternalReturnRecovery(projectRef: string): boolean {
  return (
    Boolean(readPersistedAuthSessionRaw(projectRef))
    || Boolean(readAuthSessionBackupRaw())
    || hasRecentBillingExternalNavigation()
  )
}

export function registerAuthSessionPageshowRecovery(client: SupabaseClient): void {
  if (typeof window === 'undefined') return

  const projectRef = projectRefFromEnv()

  window.addEventListener('pageshow', () => {
    if (!shouldAttemptExternalReturnRecovery(projectRef)) return
    void recoverSessionFromStorage(client, projectRef)
  })
}

/** After an inactive tab wakes, refresh session once and report auth failures to Sentry. */
export function registerAuthVisibilityWakeRecovery(client: SupabaseClient): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  let wakeInFlight = false

  const handleVisibilityWake = async () => {
    if (document.visibilityState !== 'visible' || wakeInFlight) return
    wakeInFlight = true
    beginVisibilityWakeRecovery()
    try {
      const { data: { session } } = await client.auth.getSession()
      if (!session?.access_token) {
        return
      }

      const { error } = await client.auth.refreshSession()
      if (error) {
        const status = typeof error.status === 'number' ? error.status : 401
        captureAuthUnauthorizedFailure('visibility_wake', status, error.message)
      }
    } finally {
      wakeInFlight = false
    }
  }

  document.addEventListener('visibilitychange', () => {
    void handleVisibilityWake()
  })
}

export async function openExternalBillingUrl(
  url: string,
  client: SupabaseClient,
  projectRef: string = projectRefFromEnv(),
): Promise<void> {
  await ensureSessionPersistedBeforeExternalRedirect(client, projectRef)
  markBillingExternalNavigation()
  const opened = window.open(url, '_blank', 'noopener,noreferrer')
  if (!opened) {
    window.location.assign(url)
  }
}
